import type { Cadence, MockSectionCustomerInput } from '@act/shared/data-transfer-objects';

/** Props passed to Studio-hosted full-bleed section components in the reading column. */
export interface ReportHostedSectionProps {
  /** Resolved snapshot JSON (mock or published). */
  data: Record<string, unknown>;
  customer: MockSectionCustomerInput;
  reportingPeriod: string;
  /** Warehouse measurement period from report settings (hero eyebrow). */
  measurementPeriod?: string | null;
  /** Report uuid for live warehouse section reads. */
  reportId?: string;
  /** Report cadence (warehouse county metrics are monthly/quarterly only). */
  cadence?: Cadence;
  /** Author-edited callout body (optional; hero renders inline). */
  callout?: string | null;
  /** Optional custom callout heading; null uses the default label. */
  calloutTitle?: string | null;
  /** Whether the callout block is enabled for this row. */
  calloutEnabled?: boolean;
  /** Whether the clinical insight card is shown for this row. */
  clinicalInsightEnabled?: boolean;
  /** Whether the financial insight card is shown for this row. */
  financialInsightEnabled?: boolean;
  /** Builder: edit callout inline in hosted sections (hero cover). */
  authoringEditable?: boolean;
  /** @param value - Updated callout text. */
  onCalloutChange?: (value: string) => void;
  /** @param value - Updated callout title. */
  onCalloutTitleChange?: (value: string) => void;
}

const OUT_OF_FIVE_PATTERN = /^([0-9]+(?:\.[0-9]+)?)\s*\/\s*5\s*$/;

/**
 * Parses a KPI tile display value into a number for animated counters.
 * @param raw - Tile value string (may include %, commas, or `/5`).
 * @returns Numeric value or 0 when not parseable.
 */
export const parseKpiTileNumber = (raw: string): number => {
  const trimmed = raw.trim();
  const outOfFive = trimmed.match(OUT_OF_FIVE_PATTERN);
  if (outOfFive) {
    const parsed = Number.parseFloat(outOfFive[1]);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Extracts suffix characters after the numeric portion (e.g. `%`, `/5`).
 * @param raw - Tile value string.
 * @returns Suffix for {@link AnimatedCounter}.
 */
export const kpiTileSuffix = (raw: string): string => {
  const trimmed = raw.trim();
  if (OUT_OF_FIVE_PATTERN.test(trimmed)) {
    return '/5';
  }
  const match = trimmed.match(/[0-9][0-9,.\s]*([^\d,.\s]+)$/);
  return match?.[1] ?? '';
};
